import React, { useEffect, useMemo, useState } from 'react';
import { CloseIcon, CollapseIcon, MenuIcon } from '../common/icon';
import NavbarContent from './NavbarContent';
import ModalBox from './ModalBox';
import { useInfluencerInfo } from '@/hooks/useInfluencerInfo';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { useSettingsStore } from '@/hooks/useSettingsStore';
import { useBaselineConfig } from '@/hooks/useBaselineConfig';
import {
  clearStoredLeaderboardShareId,
  clearSignupReferralCodeInput,
  getHomeUrl,
  getKeyFromUrlSearch,
  getStoredLeaderboardShareId,
  getSignupReferralCodeInput,
  getStoredReferralCode,
  isMobile,
  localStorageSaveUtmMediumAndSource,
  maskEmail,
  notMatch
} from '@/biz/tool';
import axiosInstance from '@/apis/axiosInstance';
import { useLoginStore } from '@/hooks/useLoginStore';
import {
  getUserLang,
  handleLangConflict,
  normalizeToSupportedLang
} from '@/i18n/utils';
import { useLocation } from 'react-router-dom';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Drawer from '@material-ui/core/Drawer';
import { Tooltip } from 'aha-components';

export default function NewNavbar() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const { refresh: fetchInfluencerInfo } = useInfluencerInfo();
  const {
    closeModalEmailVerification,
    showContactInformationModal,
    setShowModalEmailVerification,
    setFirstApplyVerifyEmailVerified,
    setHasAppliedOrder
  } = useWebModalStore();
  const { setInfluencers, setUser } = usePlatformStore();
  const { setCollaborationConfig } = useSettingsStore();
  const { setBaselineConfig } = useBaselineConfig();
  const router = useLocation();
  const { detailData } = useDetailDataStore();
  const pathname = window.location.pathname;
  /** 优先使用注册页输入框的邀请码（OAuth 回调后 URL 常无 REFERRALCODE，缓存又是旧值） */
  const REFERRALCODE =
    getKeyFromUrlSearch('REFERRALCODE') || getStoredReferralCode() || '';
  const getReferralCodeToSend = () =>
    getSignupReferralCodeInput() ?? REFERRALCODE;
  const { setChannelAvatar, setChannelName, setUserEmail, setUserAddressInfo } =
    useLoginStore();
  // 这里强制 sync 到 localStorage，例如有非法值或空值
  const initUserLang = getUserLang(true).detectedValue;

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const sidebarClassName = useMemo(() => {
    return collapsed
      ? 'hidden md:flex flex-col min-h-0 w-[48px] border-r border-[#E7E5E4] py-6 px-0 overflow-hidden max-h-screen transition-all duration-300'
      : 'hidden md:flex flex-col min-h-0 w-[296px] flex-shrink-0 border-r border-[#E7E5E4] py-6 px-4 overflow-hidden max-h-screen transition-all duration-300';
  }, [collapsed]);

  // 邀请码发送
  const setReferralCode = async () => {
    const codeToSend = getReferralCodeToSend();
    if (!codeToSend) return;
    try {
      const {
        data: { code, message }
      } = await axiosInstance.post(`/user/referralCode`, {
        referralCode: codeToSend
      });
      clearSignupReferralCodeInput();
    } catch (error) {
      console.error('setReferralCode error: ', error);
    }
  };

  const getLeaderboardShareIdToSend = () =>
    getKeyFromUrlSearch('leaderboardShareId') || getStoredLeaderboardShareId();

  const isRecentSignupUser = (createdAt?: string | Date) => {
    if (!createdAt) return false;
    const createdAtTime = new Date(createdAt).getTime();
    if (!Number.isFinite(createdAtTime)) return false;
    return Date.now() - createdAtTime <= 24 * 60 * 60 * 1000;
  };

  const recordLeaderboardShareSignup = async (shareId: string) => {
    if (!shareId) return;
    try {
      await axiosInstance.post('/leaderboard/api/share/signup', {
        shareId
      });
      clearStoredLeaderboardShareId();
    } catch (error) {
      console.error('recordLeaderboardShareSignup error: ', error);
    }
  };

  const refreshCreatorInfo = async () => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    const current = `${pathname}${search}`;
    try {
      const payload = await fetchInfluencerInfo();
      if (!payload) return;

      const { influencers, user } = payload;
      // 保存是否申请订单&验证邮件状态； 申请过订单 但是还没有验证邮件，则弹出验证邮件弹窗
      setFirstApplyVerifyEmailVerified(user?.firstApplyVerifyEmailVerified);
      setHasAppliedOrder(user?.hasAppliedOrder);
      if (
        !user?.firstApplyVerifyEmailVerified &&
        user.hasAppliedOrder &&
        !closeModalEmailVerification
      ) {
        setShowModalEmailVerification(true);
      }
      // 邀请码：先看 URL，注册后跳转到 / 或 /connect 时 URL 已无 REFERRALCODE，用 storage 兜底（App 在进入带 REFERRALCODE 的页时已写入）

      // console.log(window.location.pathname.includes('detail'));
      if (influencers) {
        setInfluencers(influencers);
      }
      if (user?.collaborationConfig) {
        setCollaborationConfig(user?.collaborationConfig);
      }
      if (user?.baselineConfig) {
        setBaselineConfig(user?.baselineConfig);
      }
      if (influencers && !influencers?.length) {
        // 邀请码（推荐返佣）和 leaderboard 分享归因互相独立，分别上报
        const leaderboardShareId = getLeaderboardShareIdToSend();
        if (leaderboardShareId) {
          if (isRecentSignupUser(user?.createdAt)) {
            await recordLeaderboardShareSignup(leaderboardShareId);
          } else {
            clearStoredLeaderboardShareId();
          }
        }
        if (getReferralCodeToSend()) {
          await setReferralCode();
        }
        // if (pathname === '/') {
        //   // 没找到达人信息， 需要跳转到达人连接页面
        //   window.location.replace('/connect');
        // }
      } else if (influencers?.length) {
        clearStoredLeaderboardShareId();
      }
      setUser(user);
      const info: any = influencers?.[0] || {};

      // 设置info, 登录没授权则没有
      if (info) {
        setChannelAvatar(info?.profilePictureLink);
        setChannelName(info?.name);
      }
      // 设置邮箱，登录就有
      if (user) {
        setUserEmail(user?.email);
        setUserAddressInfo(user?.addressInfo);
        // 如果 API 获取的 lang 和检测(ls)出来的不一致，则重写缓存并刷新。前提是接口下发值为默认值
        if (user.lang && user.lang !== 'auto') {
          const apiLang = normalizeToSupportedLang(user.lang);
          if (apiLang !== initUserLang) {
            handleLangConflict(user.lang, initUserLang);
          } else if (user.lang !== apiLang) {
            axiosInstance
              .post('/influencer/info', {
                lang: apiLang
              })
              .catch(() => {
                /* eat errors */
              });
          }
        } else {
          // 是默认值，静默发个请求覆盖掉
          axiosInstance
            .post('/influencer/info', {
              lang: initUserLang
            })
            .catch(() => {
              /* eat errors */
            });
        }
      }
    } catch (error) {
      console.error('Influencer information query error:', error);
      const target = getHomeUrl({ showLoginModal: true });
      if (
        !current.includes('detail') &&
        !current.includes('connect') &&
        !current.includes('contactInfo') &&
        !current.includes('/?showLoginModal=1') &&
        !current.includes('/callback') &&
        !current.includes('/error') &&
        !current.includes('/loginError') &&
        current !== target
      ) {
        // window.location.href = getHomeUrl({ showLoginModal: true });
      }
      // 存储 utm_medium
      // 存储 utm_source
      localStorageSaveUtmMediumAndSource();
      //
      if (current.includes('/?showLoginModal=1')) {
        // 清除url中的loginSuccess参数
        window.history.pushState({}, '', getHomeUrl());
      }
    }
  };

  // 监听路由变化
  useEffect(() => {
    // 全局整体鉴权
    refreshCreatorInfo();
  }, [router]);

  useEffect(() => {
    // 这里的 showContactInformationModal 是当前值
    return () => {
      // 这里的 showContactInformationModal 是变化前的值
      if (showContactInformationModal === true) {
        // 说明从 true 变为 false
        // 刷新一下 info
        refreshCreatorInfo();
      }
    };
  }, [showContactInformationModal]);

  useEffect(() => {
    // 仅在移动端时，当 detailData 更新时滚动到顶部
    if (isMobile()) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    if (detailData?.user && pathname.includes('detail')) {
      const isNotMatch = notMatch({ detailData });
      if (isNotMatch) {
        setTimeout(() => {
          if (detailData?.order?.user?.email) {
            toast.error(
              t('common.social_account_linked_email', {
                defaultValue:
                  'This social account is already linked to {{email}}. Please sign in with this email, or contact support if you need to update it.',
                email: maskEmail(detailData?.order?.user?.email)
              })
            );
          } else {
            toast.error(
              t('common.social_account_linked', {
                defaultValue:
                  'Your social account is linked to a different email address. Please use that email to sign in to the platform. If you have any further questions, feel free to contact our support team.'
              })
            );
          }
        }, 0);
      }
    }
  }, [detailData]);

  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <div className={sidebarClassName}>
        <div
          className={`flex items-center transition-all duration-300 ${
            collapsed ? 'justify-center' : 'justify-between'
          }`}
        >
          <div
            className={`transition-all duration-300 overflow-hidden ${
              collapsed ? 'w-0 opacity-0' : 'w-[173px] opacity-100'
            }`}
          >
            <img
              alt="logo"
              src="https://website-resource.ahaglobal.io/aha-brand-logo/aha-logo-name-black.png"
              className="w-[173px]"
            />
          </div>

          <Tooltip
            content={
              collapsed
                ? t('navbar.menu.expand', { defaultValue: 'Open the menu' })
                : t('navbar.menu.collapse', { defaultValue: 'Close the menu' })
            }
          >
            <button
              type="button"
              className="cursor-pointer p-1 rounded-[6px] hover:bg-[#FAFAF9] transition-colors duration-200 flex items-center justify-center w-8 h-8 bg-[#F5F5F4]"
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              <CollapseIcon
                className={`transition-transform duration-300 ${
                  collapsed ? 'text-[#A9A29D]' : 'text-black'
                }`}
              />
            </button>
          </Tooltip>
        </div>
        <div className="transition-all duration-300 flex-1 min-h-0 flex flex-col overflow-hidden">
          <NavbarContent collapsed={collapsed} />
        </div>
      </div>
      <div className="md:hidden h-[64px] flex items-center justify-between px-4 w-full">
        <img
          alt="logo"
          src="https://website-resource.ahaglobal.io/aha-brand-logo/aha-logo-name-black.png"
          className="w-[173px]"
        />
        <div
          className="cursor-pointer w-10 h-10 flex items-center justify-center"
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon className="w-6 h-6 text-[#44403C]" />
        </div>
      </div>
      <Drawer
        open={menuOpen}
        anchor={'bottom'}
        onClose={() => setMenuOpen(false)}
        PaperProps={{
          // bottom anchor 默认 top:auto + maxHeight:100%，在 iOS 上高度易算短，顶部会露出遮罩（灰条）
          style: {
            boxShadow: 'none',
            padding: '0px 16px 16px 16px',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100%',
            maxHeight: 'none'
          }
        }}
      >
        <div className="flex flex-col h-full min-h-0 pt-[env(safe-area-inset-top)]">
          <div className="h-[64px] flex-shrink-0 flex items-center justify-between w-full">
            <img
              alt="logo"
              src="https://website-resource.ahaglobal.io/aha-brand-logo/aha-logo-name-black.png"
              className="w-[173px]"
            />
            <div
              className="cursor-pointer w-10 h-10 flex items-center justify-center"
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon className="w-4 h-4 text-[#44403C]" />
            </div>
          </div>
          <NavbarContent setMenuOpen={setMenuOpen} />
        </div>
      </Drawer>
      <ModalBox />
    </>
  );
}
